import 'phaser'
import { IJobCreator } from '../behavior/jobCreators'
import { jobCreators } from '../behavior/jobCreators-sidecar'
import { IJob } from '../behavior/jobs'
import { jobs } from '../behavior/jobs-sidecar'
import { cargoes } from '../entities/cargoes-sidecar'
import { ICargoType } from '../entities/cargoesData'
import { IEntity } from '../entities/entitiesUtils'
import { entitiesUtils } from '../entities/entitiesUtils-sidecar'
import { stacks } from '../entities/stacks-sidecar'
import { assert } from '../lib/assert-sidecar'
import { distance } from '../lib/distance-sidecar'
import { l } from '../lib/imports'
import { IMachineData } from '../machines/machinesData'
import { createId } from '../main/common'
import { fi32 } from '../main/frameInfo'
import { layers } from '../main/layers-sidecar'
import { spriteMap } from '../main/spriteMap-sidecar'
import { ITileMapShip } from '../tiles/tileMapShip'
import { ITileDataShip } from '../tiles/tilesDataShip'
import { IStevieText } from '../uiParts/stevieText'
import { blueprintMine } from './blueprintMine-sidecar'
import { IBlueprintData, IBlueprintType } from './blueprintsData'
import { blueprintsData } from './blueprintsData-sidecar'
import { blueprintDeconstructHull } from './blueprintDeconstructHull-sidecar'
import { IProgressIndicator } from '../indicators/progressIndicators'
import { progressIndicators } from '../indicators/progressIndicators-sidecar'

// interface

export interface IBlueprintPart extends IEntity {
  parent: IBlueprint
  // data: IBlueprintPartData
  i: number
  j: number
  oi: number
  oj: number
  image: Phaser.GameObjects.Image
}

export interface IBlueprint extends IEntity {
  data: IBlueprintData
  parts: IBlueprintPart[]
  image: Phaser.GameObjects.Image
  imageGhost: Phaser.GameObjects.Image
  i: number
  j: number
  job: IJob | null
  tileData: ITileDataShip | null
  machineData: IMachineData | null
  cargoType: ICargoType | null
  jobCreators: IJobCreator[]
  text: IStevieText | null
  onUpdate: (obj: IBlueprint) => void

  progressIndicator: IProgressIndicator | null
}

const noopUpdate = () => {}
const objs: IBlueprint[] = []
export function getAll() {
  return objs
}
export function findById(id: number) {
  return entitiesUtils.findById(id, objs)
}
export function findClosestTo(
  i: number,
  j: number,
  filter: (c: IBlueprint) => boolean,
  invalid: IBlueprint[] = [],
) {
  return distance.findClosestTo(objs, i, j, filter, invalid)
}

export function findAtSpot(
  i: number,
  j: number,
  filter: (c: IBlueprint) => boolean,
): IBlueprint | null {
  let closest: IBlueprint | null = null
  l.forEach(objs, (c) => {
    if (filter(c)) {
      if (c.i === i && c.j === j) {
        closest = c
        return false
      }

      for (let idx = 0; idx < c.parts.length; idx++) {
        let p = c.parts[idx]
        if (p.i === i && p.j === j) {
          closest = c
          return false
        }
      }
    }
  })
  return closest
}

export function ensureType(obj: IBlueprint, type: IBlueprintType) {
  assert.eq(
    obj.data.type,
    type,
    'wrong machine type!',
    'expected',
    type,
    'got',
    obj.data.type,
  )
}

const frameBlueBackground = fi32(8, 1, 2)

export function create(type: IBlueprintType): IBlueprint {
  let id = createId()
  let data = blueprintsData.dataMapName[type]

  let imageGhost = spriteMap.createImageFromFrameInfo(
    layers.getLayers().blueprints,
    data.frame,
    0,
    0,
  )
  imageGhost.alpha = 0.5
  imageGhost.visible = false
  let image = spriteMap.createImageFromFrameInfo(
    layers.getLayers().blueprints,
    data.frame,
    0,
    0,
  )
  image.alpha = 0.5

  let obj: IBlueprint = {
    data,
    eType: 'blueprint',
    id,
    parts: [],
    image,
    imageGhost,
    tileData: null,
    machineData: null,
    cargoType: null,
    isDead: false,
    i: -1,
    j: -1,
    job: null,
    jobCreators: [],
    text: null,
    onUpdate: noopUpdate,
    progressIndicator: null,
  }
  objs.push(obj)

  if (obj.data.type === 'deconstruct-hull') {
    blueprintDeconstructHull.onCreate(obj)
  }

  if (obj.data.type === 'construct-hull') {
    let jc = jobCreators.create(
      () => {
        if (
          !cargoes.findClosestTo(obj.i, obj.j, (c) => c.data.type === 'steel')
        ) {
          return false
        }

        return true
      },
      () => {
        let j = jobs.create('construct-hull')
        j.blueprint = obj
        return j
      },
    )
    obj.jobCreators.push(jc)
  }

  if (obj.data.type === 'build') {
    let jc = jobCreators.create(
      () => {
        if (
          !cargoes.findClosestTo(obj.i, obj.j, (c) => c.data.type === 'steel')
        ) {
          return false
        }

        return true
      },
      () => {
        let j = jobs.create('build')
        j.blueprint = obj
        return j
      },
    )
    obj.jobCreators.push(jc)
  }
  if (obj.data.type === 'debuild') {
    let jc = jobCreators.create(
      () => {
        return true
      },
      () => {
        let j = jobs.create('debuild')
        j.blueprint = obj
        return j
      },
    )
    obj.jobCreators.push(jc)
  }

  if (obj.data.type === 'mine') {
    blueprintMine.onCreate(obj)
  }

  return obj
}

export function setHullPart(obj: IBlueprint, td: ITileDataShip) {
  obj.imageGhost.visible = true
  spriteMap.setFrame(obj.imageGhost, td.frameInfo)
  obj.machineData = null
  obj.tileData = td
  obj.cargoType = null
}
export function setMachine(obj: IBlueprint, data: IMachineData) {
  obj.imageGhost.visible = true
  spriteMap.setFrame(obj.imageGhost, data.frames[0])
  obj.machineData = data
  obj.tileData = null
  obj.cargoType = null

  // Create parts
  l.forEach(data.placeRules, (pr) => {
    if (pr.hasPart) {
      // Create a blueprint part
      console.log('create part', pr)
      let imagePart = spriteMap.createImageFromFrameInfo(
        layers.getLayers().blueprintsParts,
        frameBlueBackground,
        0,
        0,
      )
      imagePart.tint = 0x0000ff
      imagePart.alpha = 0.5
      let bpp: IBlueprintPart = {
        oi: pr.oi,
        oj: pr.oj,
        i: obj.i + pr.oi,
        j: obj.j + pr.oj,
        eType: 'blueprint-part',
        image: imagePart,
        isDead: false,
        id: createId(),
        parent: obj,
      }
      obj.parts.push(bpp)
    }
  })
}
export function setStack(obj: IBlueprint, cargoType: ICargoType) {
  obj.imageGhost.visible = true
  spriteMap.setFrame(obj.imageGhost, stacks.frame)
  obj.machineData = null
  obj.tileData = null
  obj.cargoType = cargoType
}
export function moveToTile(
  obj: IBlueprint,
  map: ITileMapShip,
  x: number,
  y: number,
) {
  obj.image.x = map.ox + x * map.tileSize
  obj.image.y = map.oy + y * map.tileSize
  obj.imageGhost.x = map.ox + x * map.tileSize
  obj.imageGhost.y = map.oy + y * map.tileSize
  obj.i = x
  obj.j = y

  for (let idx = 0; idx < obj.parts.length; idx++) {
    let p = obj.parts[idx]
    p.i = obj.i + p.oi
    p.j = obj.j + p.oj
    p.image.x = map.ox + p.i * map.tileSize
    p.image.y = map.oy + p.j * map.tileSize
  }
}

export function killAll() {
  objs.forEach((c) => {
    kill(c)
  })
}
export function kill(obj: IBlueprint) {
  console.log('bluepinrt', 'killed!', objs.length)
  obj.isDead = true
}

export function updateAll() {
  objs.forEach((c) => {
    update(c)
  })
  entitiesUtils.removeDead(objs, (c) => {
    console.log('bluepinrt', 'removed!', c)
    c.image.destroy()
    c.imageGhost.destroy()

    l.forEach(c.parts, (d) => {
      d.image.destroy()
    })

    if (c.progressIndicator) {
      progressIndicators.kill(c.progressIndicator)
    }

    // Bugs out on asteroids
    // c.job = jobCreators.destroyAll(c.jobCreators, c.job)
  })
}
function update(obj: IBlueprint) {
  // let ctx = scene.getContext()
  obj.job = jobCreators.runMulti(obj.jobCreators)
  if (obj.progressIndicator) {
    // progressIndicators.attach()
  }
  // console.log('jorb is', obj.job, obj.jobCreators)
}

export function setProgress(obj: IBlueprint, progress: number, max: number) {
  if (!obj.progressIndicator) {
    obj.progressIndicator = progressIndicators.create()
  }
  progressIndicators.attach(
    obj.progressIndicator,
    obj.image.x,
    obj.image.y,
    progress / max,
  )
}
